import { useMemo, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook para verificar permisos del usuario
 * @returns {object} Objeto con funciones para verificar permisos
 */
export const usePermissions = () => {
  const { user } = useAuth();

  // Verificar si el usuario tiene el rol de Administrador
  const isAdministrador = useMemo(() => {
    if (!user) return false;
    
    // Obtener roles del usuario (manejar tanto camelCase como PascalCase)
    const roles = user.Roles || user.roles || [];
    
    // Verificar si tiene el rol "Administrador" (case-insensitive)
    return roles.some(rol => {
      const nombreRol = rol.Nombre || rol.nombre || '';
      return nombreRol.toLowerCase() === 'administrador';
    });
  }, [user]);

  // Obtener permisos del usuario desde el objeto user
  const permisos = useMemo(() => {
    if (!user) {
      return [];
    }
    
    // Si es administrador, no necesitamos cargar permisos (tiene acceso completo)
    if (isAdministrador) {
      return [];
    }
    
    // Manejar tanto camelCase como PascalCase
    const permisosData = user.Permisos || user.permisos || [];
    
    const mapped = permisosData.map(p => ({
      PermisoID: p.PermisoID || p.permisoID,
      Nombre: p.Nombre || p.nombre || '',
      Modulo: p.Modulo || p.modulo || '',
      Accion: p.Accion || p.accion || '',
      Descripcion: p.Descripcion || p.descripcion || '',
    }));
    
    return mapped;
  }, [user, isAdministrador]);

  /**
   * Mapeo de módulos del frontend a módulos de la BD
   */
  const moduloMap = {
    'Empresas': ['Empresas', 'Configuración'],
    'Sucursales': ['Sucursales', 'Configuración'],
    'Clientes': ['Clientes'],
    'Productos': ['Productos'],
    'Categorias': ['Categorias', 'Categorías'],
    'Inventario': ['Inventario'],
    'Ventas': ['Ventas'],
    'Cotizaciones': ['Cotizaciones'],
    'Facturacion': ['Facturacion', 'Facturación'],
    'Usuarios': ['Usuarios'],
    'Roles': ['Roles'],
    'Permisos': ['Permisos'],
    'Reportes': ['Reportes'],
    'Auditoria': ['Auditoria', 'Auditoría'],
  };

  /**
   * Verifica si el usuario tiene un permiso específico
   * @param {string} modulo - Nombre del módulo (del frontend)
   * @param {string} accion - Acción a verificar (Create, Read, Update, Delete, Ver, Crear, etc.)
   * @returns {boolean} true si el usuario tiene el permiso
   */
  const hasPermission = (modulo, accion) => {
    // Si es administrador, tiene todos los permisos
    if (isAdministrador) {
      return true;
    }
    
    if (!modulo || !accion) return false;
    
    // Obtener los posibles nombres del módulo en la BD
    const modulosBD = moduloMap[modulo] || [modulo];
    
    // Mapeo de acciones del frontend a acciones de la BD
    const accionMap = {
      'Read': ['Read', 'read', 'Ver', 'ver', 'View', 'view'],
      'Create': ['Create', 'create', 'Crear', 'crear'],
      'Update': ['Update', 'update', 'Editar', 'editar', 'Edit', 'edit'],
      'Delete': ['Delete', 'delete', 'Eliminar', 'eliminar'],
    };
    
    const accionesBD = accionMap[accion] || [accion];
    
    return permisos.some(p => {
      const moduloMatch = modulosBD.some(m => 
        p.Modulo.toLowerCase() === m.toLowerCase() ||
        p.Modulo.toLowerCase() === m.toLowerCase().replace('í', 'i').replace('ó', 'o')
      );
      
      const accionMatch = accionesBD.some(a =>
        p.Accion.toLowerCase() === a.toLowerCase()
      );
      
      return moduloMatch && accionMatch;
    });
  };

  /**
   * Verifica si el usuario tiene al menos uno de los permisos especificados
   * @param {Array<{modulo: string, accion: string}>} permisosRequeridos - Array de permisos a verificar
   * @returns {boolean} true si el usuario tiene al menos uno de los permisos
   */
  const hasAnyPermission = (permisosRequeridos) => {
    // Si es administrador, tiene todos los permisos
    if (isAdministrador) {
      return true;
    }
    
    if (!permisosRequeridos || permisosRequeridos.length === 0) return false;
    
    return permisosRequeridos.some(p => hasPermission(p.modulo, p.accion));
  };

  /**
   * Verifica si el usuario tiene todos los permisos especificados
   * @param {Array<{modulo: string, accion: string}>} permisosRequeridos - Array de permisos a verificar
   * @returns {boolean} true si el usuario tiene todos los permisos
   */
  const hasAllPermissions = (permisosRequeridos) => {
    // Si es administrador, tiene todos los permisos
    if (isAdministrador) {
      return true;
    }
    
    if (!permisosRequeridos || permisosRequeridos.length === 0) return false;
    
    return permisosRequeridos.every(p => hasPermission(p.modulo, p.accion));
  };

  /**
   * Obtiene todos los permisos del usuario para un módulo específico
   * @param {string} modulo - Nombre del módulo
   * @returns {Array} Array de acciones permitidas para el módulo
   */
  const getModulePermissions = (modulo) => {
    if (!modulo) return [];
    
    return permisos
      .filter(p => 
        p.Modulo.toLowerCase() === modulo.toLowerCase() ||
        p.Modulo.toLowerCase() === modulo.toLowerCase().replace('í', 'i').replace('ó', 'o')
      )
      .map(p => p.Accion);
  };

  /**
   * Verifica si el usuario puede ver (Read) un módulo
   * @param {string} modulo - Nombre del módulo
   * @returns {boolean} true si el usuario puede ver el módulo
   */
  const canView = useCallback((modulo) => {
    // Si es administrador, tiene acceso completo
    if (isAdministrador) {
      return true;
    }
    
    // Si no hay usuario, no permitir acceso
    if (!user) return false;
    
    // Si no hay permisos cargados, no permitir acceso (ya no es temporal)
    if (!permisos || permisos.length === 0) {
      return false;
    }
    
    // Verificar con diferentes nombres de acción
    return hasPermission(modulo, 'Read') || 
           hasPermission(modulo, 'read') ||
           hasPermission(modulo, 'Ver') ||
           hasPermission(modulo, 'ver') ||
           hasPermission(modulo, 'View') ||
           hasPermission(modulo, 'view');
  }, [isAdministrador, user, permisos]);

  /**
   * Verifica si el usuario puede crear en un módulo
   * @param {string} modulo - Nombre del módulo
   * @returns {boolean} true si el usuario puede crear
   */
  const canCreate = (modulo) => {
    // Si es administrador, tiene acceso completo
    if (isAdministrador) {
      return true;
    }
    
    return hasPermission(modulo, 'Create') || hasPermission(modulo, 'create');
  };

  /**
   * Verifica si el usuario puede editar en un módulo
   * @param {string} modulo - Nombre del módulo
   * @returns {boolean} true si el usuario puede editar
   */
  const canUpdate = (modulo) => {
    // Si es administrador, tiene acceso completo
    if (isAdministrador) {
      return true;
    }
    
    return hasPermission(modulo, 'Update') || hasPermission(modulo, 'update');
  };

  /**
   * Verifica si el usuario puede eliminar en un módulo
   * @param {string} modulo - Nombre del módulo
   * @returns {boolean} true si el usuario puede eliminar
   */
  const canDelete = (modulo) => {
    // Si es administrador, tiene acceso completo
    if (isAdministrador) {
      return true;
    }
    
    return hasPermission(modulo, 'Delete') || hasPermission(modulo, 'delete');
  };

  return {
    permisos,
    isAdministrador, // Exponer si el usuario es administrador
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    getModulePermissions,
    canView,
    canCreate,
    canUpdate,
    canDelete,
  };
};

